import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Clock, ArrowLeft } from 'lucide-react';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import logo from '../assets/images/logo2.PNG';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

function ChangeLog() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [branding, setBranding] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [changeLogEntries, setChangeLogEntries] = useState([]);
  const [groupedEntries, setGroupedEntries] = useState([]);

  // ✅ SUBSCRIPTION PROTECTION: Check if user can view changelog
  const { canViewChangelog, tierName } = useSubscriptionFeatures(
    clubInfo?.subscriptionTier || 'basic'
  );

  // Get user and club info
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
            
            if (userData.clubId) {
              const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
              if (clubDoc.exists()) {
                const clubData = clubDoc.data();
                setClubInfo({
                  clubId: userData.clubId,
                  name: clubData.name || userData.clubName || 'Unknown Club',
                  subscriptionTier: clubData.subscriptionTier || 'basic'
                });
                
                // Load branding from settings
                const settingsRef = doc(db, 'clubs', userData.clubId, 'settings', 'general');
                const settingsDoc = await getDoc(settingsRef);
                if (settingsDoc.exists() && settingsDoc.data().branding) {
                  setBranding(settingsDoc.data().branding);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user/club data:', error);
        }
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load changelog entries from Firestore
  useEffect(() => {
    const loadChangeLog = async () => {
      if (!clubInfo?.clubId) return;
      
      try {
        const changeLogRef = collection(db, 'clubs', clubInfo.clubId, 'changelog');
        const q = query(changeLogRef, orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const entries = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Sort sections alphabetically
          const sortedSection = data.section 
            ? data.section.split(',').map(s => s.trim()).sort().join(', ')
            : data.section;
          
          entries.push({
            id: doc.id,
            action: data.action,
            date: data.date,
            allocationDate: data.allocationDate || data.date,
            actionDate: data.actionDate || data.date,
            time: data.time || data.startTime,
            actionTime: data.actionTime || data.time,
            section: sortedSection,
            type: data.type === 'training' ? 'training' : 'match',
            team: data.team,
            user: data.user,
            userRole: data.userRole,
            pitch: data.pitch || data.pitchId,
            pitchName: data.pitchName,
            duration: data.duration || 0
          });
        });
        
        setChangeLogEntries(entries);
      } catch (error) {
        console.error('Error loading changelog:', error);
      }
    };

    loadChangeLog();
  }, [clubInfo]);

  // Group entries by action, team, date, time, and action timestamp
  useEffect(() => {
    if (changeLogEntries.length === 0) {
      setGroupedEntries([]);
      return;
    }

    const grouped = [];
    const processedGroups = new Set();

    changeLogEntries.forEach((entry) => {
      const groupKey = `${entry.action}-${entry.team}-${entry.allocationDate}-${entry.time}-${entry.actionDate}-${entry.actionTime}`;
      
      if (processedGroups.has(groupKey)) {
        return;
      }
      
      const relatedEntries = changeLogEntries.filter(e => 
        e.action === entry.action &&
        e.team === entry.team &&
        e.allocationDate === entry.allocationDate &&
        e.time === entry.time &&
        e.actionDate === entry.actionDate &&
        e.actionTime === entry.actionTime
      );
      
      const allSections = relatedEntries
        .map(e => e.section)
        .filter(s => s)
        .flatMap(s => s.split(',').map(section => section.trim()))
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort()
        .join(', ');
      
      grouped.push({
        id: entry.id,
        action: entry.action,
        team: entry.team,
        type: entry.type,
        section: allSections || entry.section,
        allocationDate: entry.allocationDate,
        time: entry.time,
        duration: entry.duration,
        actionDate: entry.actionDate,
        actionTime: entry.actionTime,
        user: entry.user,
        userRole: entry.userRole,
        pitch: entry.pitch,
        pitchName: entry.pitchName,
        count: relatedEntries.length
      });
      
      processedGroups.add(groupKey);
    });

    setGroupedEntries(grouped);
  }, [changeLogEntries]);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        fontFamily: 'system-ui, sans-serif',
        display: 'flex',
        gap: '0'
      }}>
        <div style={{
          width: '280px',
          backgroundColor: '#1e3a5f',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={logo} alt="Logo" style={{ width: '40px', height: '40px' }} />
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>
              Pitchallo
            </span>
          </div>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '16px',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px' }}>
              Loading...
            </div>
          </div>
        </div>
        <div style={{
          flex: 1,
          padding: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTop: '3px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '48px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '16px' }}>
            Authentication Required
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
            Please log in to view the change log
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // ✅ SUBSCRIPTION GATE: Block non-Premium users from viewing changelog
  if (!canViewChangelog) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        fontFamily: 'system-ui, sans-serif',
        display: 'flex',
        gap: '0'
      }}>
        {/* Sidebar */}
        <div style={{
          width: '280px',
          backgroundColor: '#243665',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          height: '100vh',
          position: 'sticky',
          top: 0
        }}>
          {/* Pitchallo Logo */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <img 
              src={logo}
              alt="Pitchallo Logo"
              style={{
                width: '100%',
                maxWidth: '180px',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* Branded Club Info Card */}
          <div style={{ padding: '12px' }}>
            <div style={{
              background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}>
              {/* Club Logo + Name Row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '6px'
              }}>
                {branding?.clubLogoUrl && (
                  <img 
                    src={branding.clubLogoUrl} 
                    alt="Club Logo" 
                    style={{
                      height: '58px',
                      width: '58px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: 'white',
                  lineHeight: '1.3'
                }}>
                  {clubInfo?.name || 'Loading...'}
                </div>
              </div>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user?.email || 'Not logged in'}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'white',
                padding: '4px 10px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                display: 'inline-block',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
              </div>
            </div>
          </div>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
            <button
              onClick={() => navigate('/menu')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s',
                width: '100%',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Menu</span>
            </button>
          </div>
          <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <button
              onClick={async () => {
                await auth.signOut();
                navigate('/login');
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                color: '#fca5a5',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main Content - Premium Upgrade Prompt */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            padding: '48px',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '72px', marginBottom: '24px', opacity: 0.9 }}>
              🔒
            </div>
            
            <h2 style={{ 
              color: '#1f2937', 
              marginBottom: '16px',
              fontSize: '32px',
              fontWeight: '700'
            }}>
              Changelog - Premium Feature
            </h2>
            
            <p style={{ 
              color: '#6b7280', 
              fontSize: '18px', 
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              You're currently on the <strong>{tierName}</strong> plan.
            </p>
            
            <div style={{
              backgroundColor: '#e0f2fe',
              border: '2px solid #0ea5e9',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <p style={{ 
                color: '#0c4a6e', 
                fontSize: '16px', 
                margin: 0,
                lineHeight: '1.7',
                fontWeight: '500'
              }}>
                <strong>✨ Good news!</strong> Your changelog has been recording all changes 
                in the background. When you upgrade to Premium, you'll instantly have 
                access to your complete history.
              </p>
            </div>
            
            <div style={{ 
              textAlign: 'left', 
              marginBottom: '32px',
              padding: '0 12px'
            }}>
              <h3 style={{ fontSize: '20px', marginBottom: '20px', color: '#1f2937', fontWeight: '600' }}>
                What you'll get with Premium:
              </h3>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0,
                margin: 0
              }}>
                {[
                  'Complete audit trail of all changes',
                  'Track who made what changes and when',
                  'Filter by action type, user, and date',
                  'Export changelog data',
                  'Search through historical changes'
                ].map((feature, index) => (
                  <li key={index} style={{ 
                    padding: '12px 0',
                    color: '#374151',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#10b981', fontSize: '20px', fontWeight: 'bold', flexShrink: 0 }}>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <button
              onClick={() => navigate('/settings?tab=subscription')}
              style={{
                backgroundColor: '#0066cc',
                color: 'white',
                padding: '16px 40px',
                border: 'none',
                borderRadius: '10px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
                transition: 'all 0.2s',
                width: '100%',
                maxWidth: '300px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#0052a3';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 102, 204, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#0066cc';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 204, 0.3)';
              }}
            >
              Upgrade to Premium
            </button>
            
            <p style={{ 
              marginTop: '24px', 
              fontSize: '14px', 
              color: '#9ca3af',
              fontStyle: 'italic'
            }}>
              Questions about upgrading? Contact our support team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ PREMIUM USER: Show actual changelog
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: 'system-ui, sans-serif',
      display: 'flex',
      gap: '0'
    }}>
      {/* Sidebar */}
      <div style={{
        width: '280px',
        backgroundColor: '#243665',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0
      }}>
        {/* Pitchallo Logo */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <img 
            src={logo}
            alt="Pitchallo Logo"
            style={{
              width: '100%',
              maxWidth: '180px',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
        
        {/* Branded Club Info Card */}
        <div style={{ padding: '12px' }}>
          <div style={{
            background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
          }}>
            {/* Club Logo + Name Row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '6px'
            }}>
              {branding?.clubLogoUrl && (
                <img 
                  src={branding.clubLogoUrl} 
                  alt="Club Logo" 
                  style={{
                    height: '58px',
                    width: '58px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    flexShrink: 0
                  }}
                />
              )}
              <div style={{
                fontSize: '17px',
                fontWeight: '600',
                color: 'white',
                lineHeight: '1.3'
              }}>
                {clubInfo?.name || 'Loading...'}
              </div>
            </div>
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.email || 'Not logged in'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'white',
              padding: '4px 10px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '20px',
              display: 'inline-block',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
          <button
            onClick={() => navigate('/menu')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Menu</span>
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px'
            }}
          >
            <Clock className="w-5 h-5" />
            <span>CHANGE LOG</span>
          </div>
        </div>

        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={async () => {
              await auth.signOut();
              navigate('/login');
            }}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              color: '#fca5a5',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        padding: '24px',
        overflowY: 'auto',
        height: '100vh'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: '0'
              }}>
                Allocation Change Log
              </h1>
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Complete history of all allocation changes for {clubInfo?.name}
              </p>
            </div>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              style={{
                width: '100%',
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: isOpen ? 'white' : '#f9fafb',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!isOpen) {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isOpen ? 'white' : '#f9fafb';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Clock className="w-6 h-6 text-gray-600" />
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Change History
                </h2>
                <span style={{
                  padding: '4px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  borderRadius: '9999px'
                }}>
                  {groupedEntries.length} entries
                </span>
              </div>
              {isOpen ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>

            {isOpen && (
              <div style={{ borderTop: '1px solid #e5e7eb' }}>
                <div style={{
                  padding: '16px 24px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0
                  }}>
                    Read-only log of all allocation changes. Tracks creations and deletions with full details.
                  </p>
                </div>
                
                {groupedEntries.length === 0 ? (
                  <div style={{
                    padding: '48px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      No changes recorded yet
                    </p>
                    <p style={{ fontSize: '14px' }}>
                      Allocation changes will appear here once you start creating or deleting allocations.
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 150px 100px 100px 80px 180px 120px 200px 1fr',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      <div>Action</div>
                      <div>Team</div>
                      <div>Type</div>
                      <div>Time</div>
                      <div>Duration</div>
                      <div>Sections</div>
                      <div style={{ paddingLeft: '10px' }}>Alloc. Date</div>
                      <div style={{ paddingLeft: '10px' }}>Action Date & Time</div>
                      <div style={{ paddingLeft: '10px' }}>User (Role)</div>
                    </div>

                    <div>
                      {groupedEntries.map((entry) => (
                        <div
                          key={entry.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '80px 150px 100px 100px 80px 180px 120px 200px 1fr',
                            gap: '12px',
                            padding: '8px 16px',
                            borderBottom: '1px solid #f3f4f6',
                            fontSize: '13px',
                            lineHeight: '1.5',
                            transition: 'background-color 0.15s',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{
                            color: entry.action === 'created' ? '#15803d' : '#dc2626',
                            fontWeight: '600'
                          }}>
                            {entry.action === 'created' ? 'Added' : 'Deleted'}
                          </div>
                          <div style={{ 
                            color: '#374151',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {entry.team}
                          </div>
                          <div style={{
                            color: entry.type === 'match' ? '#dc2626' : '#2563eb',
                            fontWeight: '600',
                            fontSize: '12px'
                          }}>
                            {entry.type === 'match' ? 'Match' : 'Training'}
                          </div>
                          <div style={{ 
                            color: '#374151',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}>
                            {entry.time || '-'}
                          </div>
                          <div style={{ 
                            color: '#6b7280',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {entry.duration ? `${entry.duration}m` : '-'}
                          </div>
                          <div style={{ 
                            color: '#6b7280',
                            whiteSpace: 'nowrap'
                          }}>
                            {entry.section}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '12px' }}>
                            {entry.allocationDate || entry.date}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '12px' }}>
                            {entry.actionDate || entry.date} {entry.actionTime || entry.time}
                          </div>
                          <div style={{ 
                            color: '#9ca3af',
                            fontSize: '12px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {entry.user} ({entry.userRole})
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        margin: 0
                      }}>
                        Showing {groupedEntries.length} entries • Read-only audit log
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ChangeLog;
